/** Pure helpers for building `ReportSpec` objects. Specs are immutable: helpers copy rather than edit. */

import type {
  ImportedScreen,
  ReportQueryRequest,
  ReportSpec,
  ReportTemplate,
  RowSelector,
  RowStyle
} from 'src/components/star-grid/api/reportEngineTypes';

/** Row set used when a report takes every member of its row hierarchy. */
export const DEFAULT_ROW_SELECTOR: RowSelector = { kind: 'all_leaves' };

/** The import payload's row presentation, when the template is an import. */
export const importedRowStyles = (template: ReportTemplate): { row_styles?: RowStyle[]; row_displays?: Record<string, string> } => {
  const imported = template as Partial<ImportedScreen>;
  const displays = imported.row_displays;
  return {
    ...(imported.row_styles && imported.row_styles.length > 0 ? { row_styles: imported.row_styles.map((style) => ({ ...style })) } : {}),
    ...(displays && Object.keys(displays).length > 0 ? { row_displays: { ...displays } } : {})
  };
};

/**
 * A fresh spec from a template. The template's own `row_selector` wins over the whole-hierarchy default:
 * it is the deck's pre-resolved, ordered member list, which `all_leaves` would silently replace.
 */
export function specFromTemplate(template: ReportTemplate, name: string): ReportSpec {
  return {
    name,
    group: template.group,
    cube_source: template.cube_source,
    row_axis: {
      dimension: template.row_dimension,
      hierarchy: template.row_hierarchy,
      selector: template.row_selector ? { ...template.row_selector } : DEFAULT_ROW_SELECTOR
    },
    column_axis: template.column_axis.map((entry) => ({ ...entry })),
    filters: template.filters.map((filter) => ({ ...filter, members: [...filter.members] })),
    ...(template.column_tuples ? { column_tuples: template.column_tuples.map((values) => [...values]) } : {}),
    ...importedRowStyles(template)
  };
}

/** True once a spec has everything the API needs to be saved or previewed. */
export function isSpecComplete(spec: ReportSpec): boolean {
  return Boolean(spec.name.trim() && isSpecPreviewable(spec));
}

// A dataset report reads its dataset rather than a cube, so it states no `cube_source` and is still runnable.
export function isSpecPreviewable(spec: ReportSpec): boolean {
  return Boolean((spec.cube_source || spec.dataset?.id) && spec.row_axis.dimension && spec.row_axis.hierarchy);
}

/**
 * The query body for a saved report. `filters` is the query knobs object, not the spec's dimension
 * filters (those are read server-side); every other knob is sent only when set, so the server default stands.
 */
export function savedReportQuery(request: Partial<ReportQueryRequest>): ReportQueryRequest {
  return {
    deck_type: request.deck_type ?? '',
    filters: request.filters ?? {},
    periods: request.periods ?? [],
    ...(request.fact_table ? { fact_table: request.fact_table } : {}),
    ...(request.columns ? { columns: request.columns } : {}),
    ...(request.filter_overrides ? { filter_overrides: request.filter_overrides } : {}),
    ...(request.show_by ? { show_by: request.show_by } : {}),
    ...(request.as_of ? { as_of: request.as_of } : {}),
    ...(request.view_overrides ? { view_overrides: request.view_overrides } : {}),
    ...(request.suppress_zero_rows !== undefined ? { suppress_zero_rows: request.suppress_zero_rows } : {})
  };
}
